import { useState, useRef, useEffect } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ProjectCard } from "./ProjectCard";
import { Project, ProjectStatus } from "@/types";
import { cn } from "@/lib/utils";
import { getColorFromTailwind } from "@/utils/colorUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, GripVertical } from "lucide-react";

const colorOptions = [
  { name: "Blue", value: "bg-blue-500" },
  { name: "Green", value: "bg-green-500" },
  { name: "Yellow", value: "bg-yellow-500" },
  { name: "Orange", value: "bg-orange-500" },
  { name: "Red", value: "bg-red-500" },
  { name: "Purple", value: "bg-purple-500" },
  { name: "Pink", value: "bg-pink-500" },
  { name: "Indigo", value: "bg-indigo-500" },
  { name: "Teal", value: "bg-teal-500" },
  { name: "Cyan", value: "bg-cyan-500" },
];

interface KanbanColumnProps {
  status: string;
  statusId?: string;
  statusColor?: string;
  applyColorToCards?: boolean;
  projects: Project[];
  onDeleteProject: (id: string) => void;
  onEditProject: (project: Project) => void;
  onQuickCreate?: (status: string, title: string) => void;
  onEditStatus?: (statusId: string, newName: string, newColor: string, applyColorToCards?: boolean) => void;
  onDeleteStatus?: () => void;
}

export const KanbanColumn = ({
  status,
  statusId,
  statusColor = "border-t-primary",
  applyColorToCards = false,
  projects,
  onDeleteProject,
  onEditProject,
  onQuickCreate,
  onEditStatus,
  onDeleteStatus,
}: KanbanColumnProps) => {
  const { setNodeRef } = useDroppable({ id: status });
  
  // Make column sortable if statusId is provided
  const {
    attributes,
    listeners,
    setNodeRef: setSortableNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: statusId || status,
    disabled: !statusId, // Only allow dragging if statusId exists
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Combine refs
  const combinedRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    setSortableNodeRef(node);
  };
  const [showAddButton, setShowAddButton] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [editedStatusName, setEditedStatusName] = useState(status);
  const [editedStatusColor, setEditedStatusColor] = useState(statusColor || "bg-blue-500");
  const [editedApplyColorToCards, setEditedApplyColorToCards] = useState(applyColorToCards ?? false);
  const inputRef = useRef<HTMLInputElement>(null);
  const statusInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isCreating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreating]);

  useEffect(() => {
    if (isEditingStatus && statusInputRef.current) {
      statusInputRef.current.focus();
      statusInputRef.current.select();
    }
  }, [isEditingStatus]);

  const handleQuickCreate = () => {
    if (newProjectTitle.trim() && onQuickCreate) {
      onQuickCreate(status, newProjectTitle.trim());
      setNewProjectTitle("");
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-col min-w-[320px] flex-1 h-full">
      <div
        className="mb-4 rounded-lg border-t-4 bg-card p-2 sm:p-3 shrink-0"
        style={{ borderTopColor: statusColor ? getColorFromTailwind(statusColor) : undefined }}
      >
        <div className="flex items-center justify-between gap-2">
          {/* Drag handle - only show if statusId exists and not editing */}
          {statusId && !isEditingStatus && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
              title="Drag to reorder status"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          {isEditingStatus && onEditStatus && statusId ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                ref={statusInputRef}
                type="text"
                value={editedStatusName}
                onChange={(e) => setEditedStatusName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (statusId && editedStatusName.trim() && onEditStatus) {
                      onEditStatus(statusId, editedStatusName.trim(), editedStatusColor, editedApplyColorToCards);
                    }
                    setIsEditingStatus(false);
                  } else if (e.key === 'Escape') {
                    setEditedStatusName(status);
                    setEditedStatusColor(statusColor || "bg-blue-500");
                    setEditedApplyColorToCards(applyColorToCards ?? false);
                    setIsEditingStatus(false);
                  }
                }}
                className="h-7 text-sm font-semibold"
                onBlur={(e) => {
                  // Don't close if clicking on color buttons or switch
                  const relatedTarget = e.relatedTarget as HTMLElement;
                  if (relatedTarget && (
                    relatedTarget.closest('.color-button') || 
                    relatedTarget.closest('[role="switch"]') ||
                    relatedTarget.closest('button')
                  )) {
                    return;
                  }
                  
                  // Save changes when blurring (clicking outside)
                  if (statusId && editedStatusName.trim() && onEditStatus) {
                    onEditStatus(statusId, editedStatusName.trim(), editedStatusColor, editedApplyColorToCards);
                  }
                  setIsEditingStatus(false);
                }}
              />
              <div className="flex gap-1 flex-wrap">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className="color-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const newColor = color.value;
                      setEditedStatusColor(newColor);
                      if (onEditStatus && statusId && editedStatusName.trim()) {
                        onEditStatus(statusId, editedStatusName.trim(), newColor, editedApplyColorToCards);
                      }
                    }}
                    onMouseDown={(e) => {
                      // Prevent input blur when clicking color button
                      e.preventDefault();
                    }}
                    className={cn(
                      "color-button w-5 h-5 rounded-full border-2 transition-all",
                      color.value,
                      editedStatusColor === color.value ? "border-primary ring-1 ring-primary scale-110" : "border-transparent"
                    )}
                    title={color.name}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor={`apply-color-${statusId}`} className="text-xs whitespace-nowrap">Apply</Label>
                <Switch
                  id={`apply-color-${statusId}`}
                  checked={editedApplyColorToCards}
                  onCheckedChange={(checked) => {
                    setEditedApplyColorToCards(checked);
                    if (onEditStatus && statusId && editedStatusName.trim()) {
                      onEditStatus(statusId, editedStatusName.trim(), editedStatusColor, checked);
                    }
                  }}
                  onMouseDown={(e) => {
                    // Prevent input blur when clicking switch
                    e.preventDefault();
                  }}
                />
              </div>
            </div>
          ) : (
            <h2 
              className="text-xs sm:text-sm font-semibold text-foreground truncate cursor-pointer hover:opacity-70 transition-opacity flex-1"
              onDoubleClick={() => {
                if (onEditStatus && statusId) {
                  setIsEditingStatus(true);
                  setEditedStatusName(status);
                  setEditedStatusColor(statusColor || "bg-blue-500");
                  setEditedApplyColorToCards(applyColorToCards ?? false);
                }
              }}
              title={onEditStatus ? "Double-click to edit" : undefined}
            >
              {status}
            </h2>
          )}
          {!isEditingStatus && (
            <>
              {projects.length === 0 && onDeleteStatus ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteStatus();
                  }}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  title="Delete status"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground ml-2">{projects.length}</span>
              )}
            </>
          )}
        </div>
      </div>
      <div
        ref={setNodeRef}
        className="flex-1 space-y-3 rounded-lg bg-muted/30 p-3 pb-8 min-h-[200px] flex flex-col"
        onMouseEnter={() => setShowAddButton(true)}
        onMouseLeave={() => setShowAddButton(false)}
      >
        {projects.length > 0 ? (
          <SortableContext
            items={projects.map((p) => p.id)}
            strategy={verticalListSortingStrategy}
          >
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                statusColor={statusColor}
                applyColorToCards={applyColorToCards}
                onDelete={onDeleteProject}
                onEdit={onEditProject}
              />
            ))}
          </SortableContext>
        ) : null}

        {/* Quick Add Button */}
        {(showAddButton || projects.length === 0) && !isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            className="w-full py-2 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted/50 rounded-md border-2 border-dashed border-transparent hover:border-primary/20 transition-all"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Project
          </button>
        )}

        {/* Inline Creation Form */}
        {isCreating && (
          <div className="bg-card p-3 rounded-lg shadow-sm border border-border animate-in fade-in zoom-in-95 duration-200">
            <Input
              ref={inputRef}
              placeholder="Project title..."
              value={newProjectTitle}
              onChange={(e) => setNewProjectTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleQuickCreate();
                } else if (e.key === "Escape") {
                  setIsCreating(false);
                  setNewProjectTitle("");
                }
              }}
              className="mb-2"
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsCreating(false);
                  setNewProjectTitle("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleQuickCreate}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
